import { AGENT_CARD_RESULT_KIND, drawnCardKind, QUICK_ANSWER_KINDS, type AgentCardResult, type QuickAnswerKind } from '@numeo/types'
import type {
    TmsInvoiceAgingBucket,
    TmsInvoiceDetail,
    TmsInvoiceRow,
    TmsInvoiceSendResult,
    TmsInvoiceWorklist,
    TmsLoadDocumentView,
    TmsLoadRow,
    TmsFleetAvailability,
    TmsLoadsReadiness,
    TmsWriteRow,
} from '@numeo/types-main-web-app'
import type { GeneralChatMessage } from '../../types'

/**
 * How much of a list a card's answer holds, as the list cards report it beside their rows: a page of a tab (`total`, `shown`,
 * `next_page` when more remain, and the page's first row's position) or a named read cut at its cap (`truncated`, no total).
 */
export interface ListPage {
    shown: number
    total?: number
    nextPage?: number
    /** The position of the page's first row, counted from 1; 1 on the first page and on a named read. */
    first: number
    truncated: boolean
    /** The page size the card answered with: the most one "Load more" adds. */
    limit?: number
    /** The card's own parameters less the page: what a "Load more" sends to read the next page in place. Absent when none is offered. */
    query?: Record<string, unknown>
    /** The aged tab's buckets over the whole aged set (not the page), when the card carried them. */
    aging?: TmsInvoiceAgingBucket[]
}

/** How a card's answer is drawn (plan FR-G3): a table of rows, a preview of one thing, a write's outcome per load, or a load's document. */
export type CardView =
    | { view: 'invoices'; rows: TmsInvoiceRow[]; page?: ListPage }
    | { view: 'invoice-preview'; invoices: TmsInvoiceDetail[]; page?: ListPage }
    | { view: 'loads'; rows: TmsLoadRow[]; page?: ListPage; timezone?: string }
    | { view: 'load-preview'; load: TmsLoadRow }
    | { view: 'writes'; card: string; rows: TmsWriteRow[] }
    | { view: 'document'; document: TmsLoadDocumentView }
    | { view: 'invoicing'; worklist: TmsInvoiceWorklist; quickAnswer?: QuickAnswerKind }
    | { view: 'email-results'; results: TmsInvoiceSendResult[] }
    | { view: 'readiness'; readiness: TmsLoadsReadiness }
    | { view: 'fleet'; fleet: TmsFleetAvailability }

/** A card's answer in the thread, at the time its result was stored. */
export interface ChatCard {
    id: string
    createdAt: string
    card: string | null
    view: CardView
    /** The UI ran the card itself (a drawer's write, `source: direct_card`), with no chat turn. */
    direct?: boolean
}

/**
 * The writes a v17 drawer runs and shows in place (the review drawer's accept, rate and draft; the email and factoring drawers' drafts):
 * their stored rows still tell the thread a load was invoiced, but no chat row is drawn for them (v17 adds no message on create).
 */
const IN_PLACE_WRITES: ReadonlySet<string> = new Set(['tms_invoices.create', 'tms_load_documents.accept', 'tms_loads.edit'])

/** Whether the thread draws a card: not a drawer's own write, which its drawer and the table rows already show. */
export const isDrawnCard = (card: ChatCard): boolean => !(card.direct && card.view.view === 'writes' && IN_PLACE_WRITES.has(card.view.card))

/** A load the thread has shown: its number, and its id when the card named it. */
export interface ThreadLoad {
    number: string
    id?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

/** A tool result's `AgentCardResult`, or null for any other tool's output. */
export function parseCardResult(output: string | null | undefined): AgentCardResult | null {
    if (!output || !output.startsWith('{')) return null
    try {
        const parsed: unknown = JSON.parse(output)
        if (!isRecord(parsed) || parsed.kind !== AGENT_CARD_RESULT_KIND || !isRecord(parsed.structured)) return null
        return {
            kind: AGENT_CARD_RESULT_KIND,
            card: typeof parsed.card === 'string' ? parsed.card : null,
            text: String(parsed.text ?? ''),
            structured: parsed.structured,
        }
    } catch {
        return null
    }
}

const rowsOf = (structured: Record<string, unknown>): Record<string, unknown>[] | null =>
    Array.isArray(structured.rows) && structured.rows.every(isRecord) ? (structured.rows as Record<string, unknown>[]) : null

/**
 * The Amazon-billed rows of a readiness answer. The answer holds them as `{ count, rows }`; a card stored before that held the rows
 * as a bare array, and one stored before the Amazon group existed holds none, so both still draw.
 */
export const amazonRowsOf = (value: TmsLoadsReadiness['billed_through_amazon'] | TmsInvoiceRow[] | undefined): TmsInvoiceRow[] =>
    Array.isArray(value) ? value : (value?.rows ?? [])

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0

/** The suggestion a stored worklist answer came from (`ready`, `attention`, `submit`), when it came from one. */
const quickAnswerOf = (structured: Record<string, unknown>): QuickAnswerKind | undefined => QUICK_ANSWER_KINDS.find((kind) => kind === structured.quick_answer)

/** One direct send's stored answer: an invoice number, its outcome and what it was sent to. */
const isSendResult = (structured: Record<string, unknown>): boolean =>
    typeof structured.invoice_number === 'string' && typeof structured.outcome === 'string' && Array.isArray(structured.to)

/**
 * The list's totals a card answered beside its rows; undefined when it carries none (an older answer). A request whose page size the
 * asker set (`bounded`) is the whole set it asked for: no total, no next page, so the chat offers no more of it.
 */
export function pageOf(structured: Record<string, unknown>): ListPage | undefined {
    if (!isCount(structured.shown)) return undefined
    const page = isCount(structured.page) && structured.page > 0 ? structured.page : 1
    const limit = isCount(structured.limit) ? structured.limit : 0
    const first = (page - 1) * limit + 1
    if (structured.bounded === true) return { shown: structured.shown, first, truncated: false }
    return {
        shown: structured.shown,
        ...(isCount(structured.total) ? { total: structured.total } : {}),
        ...(isCount(structured.next_page) ? { nextPage: structured.next_page } : {}),
        first,
        truncated: structured.truncated === true,
        ...(limit ? { limit } : {}),
        ...(isRecord(structured.query) ? { query: structured.query } : {}),
        ...(Array.isArray(structured.aging) ? { aging: structured.aging as TmsInvoiceAgingBucket[] } : {}),
    }
}

/** One row is a preview only when it is the whole list: a page or a cut read of more stays a table, with its totals. */
const isWholeList = (page: ListPage | undefined): boolean => !page || (!page.truncated && (page.total === undefined || page.total <= 1))

/**
 * Which view a card's answer gets; null when the chat leaves it to the model's own words. WHETHER it is drawn is `drawnCardKind`
 * (`@numeo/types`), the rule general-agent-service also reads to keep the model from repeating it; this picks the view's shape.
 */
export function viewOfCard(card: string | null, structured: Record<string, unknown>): CardView | null {
    if (card === 'tms_invoices.send' && isSendResult(structured)) return { view: 'email-results', results: [structured as unknown as TmsInvoiceSendResult] }
    const kind = drawnCardKind(card, structured)
    if (kind === 'document') return { view: 'document', document: structured as unknown as TmsLoadDocumentView }
    if (kind === 'worklist') return { view: 'invoicing', worklist: structured as unknown as TmsInvoiceWorklist, quickAnswer: quickAnswerOf(structured) }
    if (kind === 'readiness') return { view: 'readiness', readiness: structured as unknown as TmsLoadsReadiness }
    if (kind === 'fleet') return { view: 'fleet', fleet: structured as unknown as TmsFleetAvailability }
    const rows = rowsOf(structured) ?? []
    // A write card's answer names the card itself and carries one outcome per load (TmsWriteResult).
    if (kind === 'writes')
        return { view: 'writes', card: typeof structured.card === 'string' ? structured.card : (card ?? ''), rows: rows as unknown as TmsWriteRow[] }
    const page = pageOf(structured)
    const paged = page ? { page } : {}
    if (kind === 'invoices') {
        if (rows.every((row) => Array.isArray(row.lines))) return { view: 'invoice-preview', invoices: rows as unknown as TmsInvoiceDetail[], ...paged }
        return { view: 'invoices', rows: rows as unknown as TmsInvoiceRow[], ...paged }
    }
    if (kind === 'loads') {
        const loads = rows as unknown as TmsLoadRow[]
        const timezone = typeof structured.timezone === 'string' ? { timezone: structured.timezone } : {}
        return loads.length === 1 && isWholeList(page) ? { view: 'load-preview', load: loads[0] } : { view: 'loads', rows: loads, ...paged, ...timezone }
    }
    return null
}

/** Adjacent direct sends draw as one results table: the sends of one "Send" are stored one by one. */
const mergeEmailResults = (cards: ChatCard[]): ChatCard[] =>
    cards.reduce<ChatCard[]>((merged, card) => {
        const last = merged[merged.length - 1]
        if (last?.view.view === 'email-results' && card.view.view === 'email-results') {
            merged[merged.length - 1] = { ...last, view: { view: 'email-results', results: [...last.view.results, ...card.view.results] } }
        } else merged.push(card)
        return merged
    }, [])

/** The cards a tab's tool results draw, oldest first. */
export function cardsOfRows(rows: GeneralChatMessage[]): ChatCard[] {
    return mergeEmailResults(
        rows.flatMap((row) => {
            const result = parseCardResult(row.tool_output)
            const view = result && viewOfCard(result.card, result.structured)
            if (!view) return []
            const card: ChatCard = { id: row.id, createdAt: row.created_at, card: result.card, view }
            return [row.source === 'direct_card' ? { ...card, direct: true } : card]
        })
    )
}

/** Every load the cards showed, newest first, once each; an id is kept from whichever card named it. */
export function threadLoadsOf(cards: ChatCard[]): ThreadLoad[] {
    const byNumber = new Map<string, ThreadLoad>()
    const add = (number: string | null | undefined, id?: string) => {
        if (!number) return
        const known = byNumber.get(number)
        byNumber.set(number, { number, id: known?.id ?? id })
    }
    for (const { view } of [...cards].reverse()) {
        // A Ready-tab row is one load and names its id; an invoice row may hold several and names none.
        if (view.view === 'invoices')
            view.rows.forEach((row) => row.load_numbers.forEach((n) => add(n, row.load_numbers.length === 1 ? row.load_id : undefined)))
        else if (view.view === 'invoice-preview') view.invoices.forEach((inv) => inv.load_numbers.forEach((n) => add(n)))
        else if (view.view === 'loads') view.rows.forEach((row) => add(row.load_number, row.load_id))
        else if (view.view === 'load-preview') add(view.load.load_number, view.load.load_id)
        else if (view.view === 'document') add(view.document.load_number, view.document.load_id)
        else if (view.view === 'invoicing') {
            const { ready, needs_documents: needs, delivery_passed: passed } = view.worklist
            ;[...ready.rows, ...needs.rows].forEach((row) => add(row.load_numbers[0], row.load_id))
            passed?.loads.forEach((load) => add(load.load_number, load.load_id))
        } else if (view.view === 'readiness') {
            const { ready, needs_documents: needs, invoiced, not_delivered: away, ambiguous } = view.readiness
            ;[...ready, ...needs].forEach((row) => add(row.load_numbers[0], row.load_id))
            invoiced.rows.forEach((row) => row.load_numbers.forEach((n) => add(n)))
            away.forEach((row) => add(row.load_number, row.load_id))
            ambiguous.forEach((a) => a.candidates.forEach((row) => add(row.load_number, row.load_id)))
        } else if (view.view === 'fleet' || view.view === 'email-results') continue
        else view.rows.forEach((row) => add(row.load_number, row.load_id))
    }
    return [...byNumber.values()]
}

/** A thread item: a message bubble or a card, in the order they happened. */
export type ThreadItem = { kind: 'message'; message: GeneralChatMessage } | { kind: 'card'; card: ChatCard }

/** Messages and cards by time; a card stored at the same moment as a message goes before it (the reply follows its tools). */
export function threadOf(messages: GeneralChatMessage[], cards: ChatCard[]): ThreadItem[] {
    const items: Array<{ item: ThreadItem; at: number; order: number }> = [
        ...cards.map((card, order) => ({ item: { kind: 'card' as const, card }, at: Date.parse(card.createdAt), order })),
        ...messages.map((message, i) => ({ item: { kind: 'message' as const, message }, at: Date.parse(message.created_at), order: cards.length + i })),
    ]
    return items.sort((a, b) => a.at - b.at || a.order - b.order).map(({ item }) => item)
}
